import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { SupervisedUserCircleOutlined } from '@mui/icons-material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog, SectionCard } from './ui';

interface GroupInvitation {
  groupId: string;
  groupName: string;
  kind: 'buying_group' | 'multi_store';
  invitedAt: string | null;
}

interface GroupMembership {
  groupId: string;
  groupName: string;
  kind: string;
}

interface GroupMine {
  invitation: GroupInvitation | null;
  membership: GroupMembership | null;
}

const GROUP_MINE_QUERY_KEY = ['group-mine'];

/**
 * The Store Settings card for this store's side of a managed group: an open
 * invitation to accept or decline, or — once accepted — which group manages
 * this store and the way to leave it. Absent when there is neither.
 */
export const GroupInvitationCard: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);

  const { data } = useQuery({
    queryKey: GROUP_MINE_QUERY_KEY,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/group/mine', { headers });
      // A 403 means the group feature is off for this store, or it has no
      // group relation at all — that is not an error to show, just no card.
      if (res.status === 403) return { invitation: null, membership: null } as GroupMine;
      if (!res.ok) {
        // Anything else (5xx, network hiccup surfaced as a bad response) is a
        // real failure; log it instead of silently rendering as "no invitation".
        console.warn('Failed to load group invitation status', res.status);
        return { invitation: null, membership: null } as GroupMine;
      }
      const body = await res.json().catch(() => ({}));
      if (!body?.success) {
        console.warn('Failed to load group invitation status', body?.message);
        return { invitation: null, membership: null } as GroupMine;
      }
      return body.data as GroupMine;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const decide = useMutation({
    mutationFn: async ({ groupId, accept }: { groupId: string; accept: boolean }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(
        `/group/invitations/${groupId}/${accept ? 'accept' : 'decline'}`,
        { method: 'POST', headers },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || 'Could not answer the invitation');
      return accept;
    },
    onSuccess: (accepted) => {
      showToast(accepted ? t('store.group.accepted') : t('store.group.declined'), 'success');
      queryClient.invalidateQueries({ queryKey: GROUP_MINE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['session-organizations'] });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const leave = useMutation({
    mutationFn: async (groupId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/group/${groupId}/leave`, { method: 'POST', headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        throw new Error(body?.error?.message || body?.message || 'Could not leave the group');
      }
      return body.data;
    },
    onSuccess: () => {
      showToast(t('store.group.membership.left'), 'success');
      setLeaveConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: GROUP_MINE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['session-organizations'] });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const invitation = data?.invitation;
  const membership = data?.membership;

  if (invitation) {
    return (
      <SectionCard
        title={t('store.group.invitationTitle')}
        leading={<SupervisedUserCircleOutlined color="primary" />}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t('store.group.invitationBody', { group: invitation.groupName })}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ groupId: invitation.groupId, accept: true })}
            >
              {t('store.group.accept')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ groupId: invitation.groupId, accept: false })}
            >
              {t('store.group.decline')}
            </Button>
          </Stack>
        </Stack>
      </SectionCard>
    );
  }

  if (membership) {
    return (
      <SectionCard
        title={t('store.group.membership.title')}
        leading={<SupervisedUserCircleOutlined color="primary" />}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t('store.group.membership.body', { group: membership.groupName })}
          </Typography>
          <Stack direction="row">
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={leave.isPending}
              onClick={() => setLeaveConfirmOpen(true)}
            >
              {t('store.group.membership.leave')}
            </Button>
          </Stack>
        </Stack>
        <ConfirmDialog
          open={leaveConfirmOpen}
          title={t('store.group.membership.leaveConfirmTitle', { group: membership.groupName })}
          message={t('store.group.membership.leaveConfirmBody', { group: membership.groupName })}
          confirmLabel={t('store.group.membership.leave')}
          confirmColor="error"
          loading={leave.isPending}
          onConfirm={() => leave.mutate(membership.groupId)}
          onClose={() => setLeaveConfirmOpen(false)}
        />
      </SectionCard>
    );
  }

  return null;
};

export default GroupInvitationCard;
